/**
 * client/src/lib/use-step-up-aal3.ts
 *
 * Standalone client hook for the AAL3 WebAuthn step-up ceremony (#1612 —
 * completes #1543). Landed as its OWN module — deliberately decoupled from
 * `client/src/pages/guardian-stepup/hooks/use-stepup.ts` (a different feature,
 * the guardian-respond handshake), per the #1612 scope decision, even though
 * the never-implemented plan doc at
 * docs/superpowers/plans/2026-05-20-guardian-respond-aal3-gate/03-client-stepup-hook.md
 * originally proposed adding it there.
 *
 * Drives the REAL, already-live-and-tested server ceremony — no new server
 * route for the SINGLE-assertion arm, no new crypto:
 *   POST /api/auth/webauthn/step-up/begin    -> { mode, options } (server/logic/webauthn/step-up.ts:beginStepUp)
 *   navigator.credentials.get(...)           -> assertion         (via @simplewebauthn/browser)
 *   POST /api/auth/webauthn/step-up/complete -> { newAal }        (server/logic/webauthn/step-up.ts:completeStepUp)
 *
 * Owner-directed dual-passkey addition: when `begin.mode === "dual"` (no
 * hardware-eligible credential, but two distinct passkeys are each ≥24h old),
 * the ceremony runs a SECOND assertion in between — see
 * use-step-up-aal3-dual.ts for that arm's own module header. `phase` tracks
 * which leg is in flight ("first-key" | "second-key") so a caller can render
 * "Now tap your second passkey…" between the two prompts. Every OTHER mode
 * ("hardware" or "single", for AAL2) is the unchanged one-assertion flow —
 * `begin.mode !== "dual"` is the only branch condition, so an older
 * mock/response with no `mode` field at all (or any value other than the
 * literal "dual") takes the single-assertion path, byte-identical to
 * pre-dual-passkey behaviour.
 *
 * Mirrors the proven ceremony pattern already used by useStampCeremony
 * (client/src/lib/use-stamp-ceremony.ts) and StepUpGate
 * (client/src/components/challenges/StepUpGate.tsx):
 *   - decodePrfExtensionInputs MUST run before startAuthentication (v13 of
 *     @simplewebauthn/browser does not decode base64url PRF eval inputs).
 *   - isUserCancel checks BOTH err.name and err.cause.name for NotAllowedError
 *     (simplewebauthn wraps the DOM exception).
 *   - No client signing keys; the client only relays the assertion(s)
 *     (Rule 11) — the server performs UV verification + the AAL-promotion
 *     side effects, for both bases.
 *
 * Public API note: `reset()` is deliberately NOT exposed. `runStepUp` clears
 * `error` to null at the very start of every invocation, so a consumer (e.g.
 * the step-up button in Aal3ElevationNotice) can re-run the ceremony without
 * an explicit clear call.
 *
 * Security note: this hook makes NO security decision. beginStepUp/
 * continueDualStepUp/completeStepUp do the AAL elevation + assertion
 * verification entirely server-side (existing, tested,
 * L0-4/L0-6/Rule-4-compliant) — the hook is a thin relay, same posture as
 * every other WebAuthn client ceremony in this codebase. The dual basis is
 * possession + freshness, TWICE — never a claim that a live human is present
 * or about who is holding either device (CLAUDE.md §AAL Thresholds).
 *
 * Error-code -> copy mapping lives in step-up-error-copy.ts (shared by both
 * arms; see that module's header for the full error-code source list).
 */

import { useState, useCallback } from "react";
import type { AuthenticationResponseJSON } from "@simplewebauthn/browser";
import { apiRequest, ApiError } from "@/lib/queryClient";
import { decodePrfExtensionInputs } from "@/lib/webauthn-prf-extension";
import { startLocalFirstAuthentication } from "@/lib/webauthn/local-credential";
import { mapStepUpError } from "@/lib/step-up-error-copy";
import { runDualCeremony } from "@/lib/use-step-up-aal3-dual";
import { stashStepUpOfficerSecrets } from "@/lib/payment-rail/stepup-prf-carry";
import type {
  StepUpBeginResponse,
  StepUpCompleteResponse,
  StepUpPhase,
} from "./use-step-up-aal3-types";

export type { StepUpPhase } from "./use-step-up-aal3-types";

const BEGIN_URL    = "/api/auth/webauthn/step-up/begin";
const COMPLETE_URL = "/api/auth/webauthn/step-up/complete";

export interface StepUpAAL3Result {
  /** Runs the full begin -> assert(s) -> complete ceremony for targetAal
   *  "AAL3". Resolves on success; re-throws the mapped error on failure (so a
   *  caller can `.catch()` for control flow while `error` drives the UI copy).
   *  `opts.basis: "dual"` explicitly requests the two-passkey basis (ledger
   *  #86 — e.g. the certified key isn't in hand); absent keeps the server's
   *  hardware-first precedence. Only the literal "dual" on an options OBJECT
   *  is forwarded, so passing this function directly as an event handler can
   *  never leak an event object into the request body. */
  runStepUp: (opts?: { basis?: "dual" }) => Promise<void>;
  isRunning: boolean;
  /** "first-key"/"second-key" only while a dual-basis ceremony is mid-flight
   *  between its two prompts — a caller can use this to swap button copy
   *  ("Now tap your second passkey…"). Always "idle" for the single-assertion
   *  arm and whenever nothing is running. */
  phase: StepUpPhase;
  /** Amber-tier (never red — CLAUDE.md colour contract), user-readable copy.
   *  null when idle or mid-run with no prior failure. */
  error: string | null;
  /**
   * Raw server error code behind `error`, when the failure came from the
   * server (`NO_HARDWARE_KEY` / `NO_CREDENTIALS` / `WRONG_AUTHENTICATOR_TYPE`
   * / a `DUAL_*` code / any other ApiError code). null for a user-cancel, a
   * network failure, or while idle/running. Consumers (Aal3ElevationNotice)
   * use this — not string matching on `error` — to decide whether the
   * step-up ceremony is structurally unavailable on this device
   * (NO_HARDWARE_KEY / WRONG_AUTHENTICATOR_TYPE) and a different remedy
   * (e.g. /bespoke-seeding) should be offered instead of a retry button.
   */
  errorCode: string | null;
}

/**
 * @param targetAal Which assurance level the ceremony targets. Defaults to
 * "AAL3" (the six original reveal/co-sign call sites). "AAL2" is used by pages
 * that only need to lift an AAL1 session to a confirmed-possession state (e.g.
 * the guardian-invite auth gate) — the AAL3-specific error branches
 * (NO_HARDWARE_KEY / WRONG_AUTHENTICATOR_TYPE / DUAL_*) cannot fire at AAL2,
 * so the shared copy stays correct.
 */
export function useStepUpAAL3(targetAal: "AAL2" | "AAL3" = "AAL3"): StepUpAAL3Result {
  const [isRunning, setIsRunning] = useState(false);
  const [phase, setPhase] = useState<StepUpPhase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const runStepUp = useCallback(async (opts?: { basis?: "dual" }): Promise<void> => {
    // Defensive narrow (see the interface doc): only the literal "dual" read
    // off a real options object is ever forwarded.
    const basis = opts?.basis === "dual" ? "dual" : undefined;
    setIsRunning(true);
    setPhase("idle");
    setError(null); // public auto-clear contract — do not move or guard this call
    setErrorCode(null);

    try {
      let begin: StepUpBeginResponse;
      try {
        begin = await apiRequest<StepUpBeginResponse>(BEGIN_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ targetAal, ...(basis ? { basis } : {}) }),
        });
      } catch (err) {
        setError(mapStepUpError(err, "Couldn't start verification. Try again."));
        setErrorCode(err instanceof ApiError ? (err.code ?? null) : null);
        throw err;
      }

      if (begin.mode === "dual") {
        await runDualCeremony(begin, targetAal, setPhase, setError, setErrorCode);
      } else {
        await runSingleCeremony(begin, targetAal, setError, setErrorCode);
      }

      setError(null);
      setErrorCode(null);
    } finally {
      setPhase("idle");
      setIsRunning(false);
    }
  }, [targetAal]);

  return { runStepUp, isRunning, phase, error, errorCode };
}

/** The pre-dual-passkey single-assertion arm — hardware AAL3, or AAL2.
 *  Unchanged shape from before the dual-passkey addition. */
async function runSingleCeremony(
  begin: StepUpBeginResponse,
  targetAal: "AAL2" | "AAL3",
  setError: (v: string | null) => void,
  setErrorCode: (v: string | null) => void,
): Promise<void> {
  const decodedOptions = decodePrfExtensionInputs(begin.options);

  let assertionResponse: AuthenticationResponseJSON;
  try {
    // Same routing preference as sign-in (@/lib/webauthn/local-credential).
    // Inert here whenever the preferred credential is not in THIS begin's
    // offer — on the "hardware" arm that means a platform passkey never
    // matches, though a session opened WITH a security key can. Either way the
    // preference is a strict subset of what the server offered, so this arm's
    // MDS + cross-platform gate is unaffected; it can only fail closed.
    assertionResponse = await startLocalFirstAuthentication(decodedOptions);
  } catch (err) {
    setError(mapStepUpError(err, "Verification couldn't complete. Try again."));
    throw err;
  }

  try {
    await apiRequest<StepUpCompleteResponse>(COMPLETE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ response: assertionResponse, targetAal }),
    });
  } catch (err) {
    setError(mapStepUpError(err, "Verification failed. Try again."));
    setErrorCode(err instanceof ApiError ? (err.code ?? null) : null);
    throw err;
  }

  // This assertion already carried PRF output (begin.ts seeds `prf.eval.first`
  // with the profile salt on the hardware/single arms). Hold the officer keys
  // it derives so the gated read this step-up unblocked does not prompt for the
  // same key a second time — see stepup-prf-carry.ts for the single-use, TTL
  // and credential-pin bounds, and for why nothing consumes it implicitly.
  //
  // AFTER the complete call, never before: the server must have verified the
  // assertion and promoted the session first, so a rejected ceremony can never
  // leave usable keys behind.
  stashStepUpOfficerSecrets({ assertionResponse, options: begin.options });
}
